<template>
  <!-- 科室下拉：v-model 绑定的是「科室名字符串」（各业务表存名字，不是 id） -->
  <el-select
    :value="value"
    :placeholder="placeholder"
    :clearable="clearable"
    filterable
    default-first-option
    :filter-method="filterDept"
    :loading="loading"
    class="department-select"
    @change="onChange"
  >
    <el-option
      v-for="d in filtered"
      :key="d.id"
      :label="d.name"
      :value="d.name"
    />
    <!-- 空态 + 提示：新增科室不在这里做，避免各表单都要带一套新增逻辑 -->
    <div class="dept-tip">新增科室请到 用户管理-科室管理</div>
  </el-select>
</template>

<script>
import departmentApi from '../api/department'
import { pinyin } from 'pinyin-pro'

export default {
  name: 'DepartmentSelect',
  props: {
    // v-model 绑定的科室名字符串
    value: { type: String, default: '' },
    placeholder: { type: String, default: '请选择或输入科室' },
    clearable: { type: Boolean, default: true },
    // 可选：外部直接注入选项 [{ id, name }]。
    // 传入后**不再请求科室字典接口**，用于「科室名不在字典表里」的场景
    // （例如监护统计的合并显示名「内二内七科」，字典表里只有「内二科」「内七科」）。
    // 不传时保持原有行为，6 处调用点无需改动。
    optionList: { type: Array, default: null },
  },
  data() {
    return {
      options: [], // 全量未删除科室（mounted 拉一次缓存）
      filtered: [], // 当前过滤结果
      loading: false,
      loaded: false,
    }
  },
  watch: {
    // 外部选项可能是异步到的（后端请求后才赋值），到了就替换并重置过滤结果
    optionList(v) {
      if (Array.isArray(v)) {
        this.options = v
        this.filtered = v
        this.loaded = true
      }
    },
  },
  mounted() {
    if (Array.isArray(this.optionList)) {
      this.options = this.optionList
      this.filtered = this.options
      this.loaded = true
      return
    }
    this.filtered = this.options
    this.fetchOptions()
  },
  methods: {
    async fetchOptions() {
      this.loading = true
      try {
        const res = await departmentApi.getDeptOptions()
        this.options = Array.isArray(res.data) ? res.data : []
        this.loaded = true
        // 默认展示全量；若当前已有值，把该值也纳入（可能是字典里没有的历史自由文本）
        this.filtered = this.options
      } catch (e) {
        // 后端未启动 / 无权限时静默失败，下拉为空，不影响表单其他字段
        this.options = []
        this.filtered = []
      } finally {
        this.loading = false
      }
    },
    // 自定义过滤：名称 / 全拼 / 拼音首字母，忽略大小写。
    // 结果按「匹配精度」排序：精确相等 > 首字母前缀 > 全拼前缀 > 名称包含 > 全拼包含 > 首字母包含。
    // 排序的目的是配合 default-first-option：输 "yk" 回车直接选中「眼科」，
    // 而不是被「内一科(nyk)/检验科(jyk)」这类子串噪音顶到第一项。
    filterDept(q) {
      const kw = (q || '').trim().toLowerCase()
      if (!kw) {
        this.filtered = this.options
        return
      }
      const scored = []
      this.options.forEach((d, idx) => {
        const name = (d.name || '').toLowerCase()
        const py = this.pinyinOf(d.name || '')
        let score = -1
        if (name === kw || py.first === kw) score = 0
        else if (py.first.indexOf(kw) === 0) score = 1
        else if (py.full.indexOf(kw) === 0) score = 2
        else if (name.indexOf(kw) !== -1) score = 3
        else if (py.full.indexOf(kw) !== -1) score = 4
        else if (py.first.indexOf(kw) !== -1) score = 5
        if (score >= 0) scored.push({ d, score, idx })
      })
      scored.sort((a, b) => a.score - b.score || a.idx - b.idx)
      this.filtered = scored.map(x => x.d)
    },
    // 拼音/首字母缓存：科室名不变，反复输入时不必重复计算
    pinyinOf(name) {
      if (!this.pyCache) this.pyCache = {}
      if (this.pyCache[name]) return this.pyCache[name]
      const full = (pinyin(name, { pattern: 'pinyin', toneType: 'none', type: 'array' }) || []).join('').toLowerCase()
      // ⚠️ 必须 type:'array' 再 join：pinyin-pro 默认返回 "n e k"（空格分隔），
      // 直接拿字符串 indexOf('nek') 永远匹配不到（2026-10-23 修复）。
      const first = (pinyin(name, { pattern: 'first', toneType: 'none', type: 'array' }) || []).join('').toLowerCase()
      this.pyCache[name] = { full, first }
      return this.pyCache[name]
    },
    onChange(val) {
      // v-model：把科室名字符串回传给父组件
      this.$emit('input', val === undefined || val === null ? '' : val)
      // 同时抛 change，供表单里 OCR 回填标记逻辑（onOcrFieldEdit）复用
      this.$emit('change', val === undefined || val === null ? '' : val)
    },
  },
}
</script>

<style scoped>
.department-select {
  width: 100%;
}
/* 下拉底部灰色提示行（el-select 下拉里的非选项节点） */
.dept-tip {
  color: #909399;
  font-size: 12px;
  padding: 6px 12px;
  border-top: 1px solid #ebeef5;
  cursor: default;
}
</style>
